/**
 * @fileoverview Componente responsável por escutar eventos de aprovação de IA e exibir feedbacks em toast.
 */

import React, { useEffect, useContext, useState, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { Button, TextField } from "@material-ui/core";
import { Brain, Check, MessageSquare, X } from "lucide-react";
import { SocketContext } from "../context/Socket/SocketContext";
import { AuthContext } from "../context/Auth/AuthContext";
import api from "../services/api";
import toastError from "../errors/toastError";

const useStyles = makeStyles(() => ({
  toastContainer: {
    padding: "12px 0",
    minWidth: 320,
    color: "#e2e8f0",
  },
  toastHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
    fontWeight: 700,
    fontSize: 13,
    color: "#7c9aff",
  },
  toastQuestion: {
    fontSize: 12,
    color: "#94a3b8",
    marginBottom: 4,
    lineHeight: 1.4,
  },
  toastResponse: {
    fontSize: 13,
    color: "#e2e8f0",
    marginBottom: 12,
    lineHeight: 1.6,
    padding: "10px 12px",
    background: "rgba(91, 124, 255, 0.10)",
    borderRadius: 10,
    borderLeft: "3px solid #5B7CFF",
    maxHeight: 140,
    overflowY: "auto",
  },
  toastActions: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  approveBtn: {
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 600,
    fontSize: 12,
    padding: "6px 16px",
    background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
    color: "#fff",
    "&:hover": {
      background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
    },
  },
  rejectBtn: {
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 600,
    fontSize: 12,
    padding: "6px 16px",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    color: "#f87171",
    "&:hover": {
      background: "rgba(239, 68, 68, 0.1)",
    },
  },
  correctBtn: {
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 600,
    fontSize: 12,
    padding: "6px 16px",
    border: "1px solid rgba(91, 124, 255, 0.3)",
    color: "#93c5fd",
    "&:hover": {
      background: "rgba(91, 124, 255, 0.1)",
    },
  },
  correctInput: {
    marginTop: 8,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      fontSize: 13,
      background: "rgba(255, 255, 255, 0.08)",
      color: "#e2e8f0",
      "& fieldset": { borderColor: "rgba(91, 124, 255, 0.2)" },
    },
    "& .MuiOutlinedInput-input": {
      padding: "8px 12px",
      color: "#e2e8f0",
    },
  },
  sendBtn: {
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 500,
    fontSize: 12,
    padding: "6px 12px",
    background: "linear-gradient(135deg, #5B7CFF 0%, #9D7CFF 100%)",
    color: "#fff",
    "&:hover": {
      background: "linear-gradient(135deg, #4a6ae8 0%, #8c6be8 100%)",
    },
  },
  // Classe global para forçar dark no toast container
  "@global": {
    ".ai-approval-toast": {
      background: "linear-gradient(135deg, #121213 0%, #161618 100%) !important",
      border: "1px solid rgba(91, 124, 255, 0.2) !important",
      borderRadius: "16px !important",
      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4) !important",
    },
    ".ai-approval-toast-body": {
      color: "#e2e8f0 !important",
      padding: "0 !important",
    },
    ".ai-approval-toast .Toastify__close-button": {
      color: "#64748b !important",
      opacity: "0.7 !important",
    },
    ".ai-approval-toast .Toastify__close-button:hover": {
      opacity: "1 !important",
    },
  },
}));

const ApprovalToastContent = ({ memory, onDismiss }) => {
  const classes = useStyles();
  const [showCorrect, setShowCorrect] = useState(false);
  const [correction, setCorrection] = useState("");
  const [correctResponse, setCorrectResponse] = useState("");
  const [loading, setLoading] = useState(false);

  const isWorkflow = memory.source === "workflow" || (!memory.agentId && memory.pendingConfirmationId);

  const handleApprove = async () => {
    setLoading(true);
    try {
      if (isWorkflow) {
        await api.put(`/pending-confirmations/${memory.pendingConfirmationId}/approve`);
      } else {
        await api.put(`/ai-agents/${memory.agentId}/memories/${memory.id}/approve`);
      }
      toast.success("Resposta aprovada e enviada!", { autoClose: 2000 });
      onDismiss();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    setLoading(true);
    try {
      if (isWorkflow) {
        await api.put(`/pending-confirmations/${memory.pendingConfirmationId}/reject`);
      } else {
        await api.delete(`/ai-agents/${memory.agentId}/memories/${memory.id}`);
      }
      toast.info("Resposta descartada.", { autoClose: 2000 });
      onDismiss();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCorrect = async () => {
    if (!correction.trim() && !correctResponse.trim()) return;
    setLoading(true);
    try {
      if (isWorkflow) {
        await api.put(`/pending-confirmations/${memory.pendingConfirmationId}/correct`, {
          correctedResponse: correctResponse.trim() || correction.trim()
        });
        toast.success("Resposta corrigida e enviada!", { autoClose: 2000 });
      } else {
        await api.put(`/ai-agents/${memory.agentId}/memories/${memory.id}/correct`, {
          correction: correction.trim(),
          approvedResponse: correctResponse.trim() || null
        });
        toast.success("Correção salva! A IA vai aprender.", { autoClose: 2000 });
      }
      onDismiss();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const truncate = (str, max) => {
    if (!str) return "";
    return str.length > max ? str.substring(0, max) + "..." : str;
  };

  return (
    <div className={classes.toastContainer}>
      <div className={classes.toastHeader}>
        <Brain size={16} />
        Agente: {memory.agentName || "IA"}
      </div>
      <div className={classes.toastQuestion}>
        <strong>Cliente:</strong> {truncate(memory.question, 100)}
      </div>
      {!showCorrect && (
        <div className={classes.toastResponse}>
          {truncate(memory.agentResponse, 200)}
        </div>
      )}
      {showCorrect ? (
        <>
          <TextField
            className={classes.correctInput}
            variant="outlined"
            fullWidth
            multiline
            rows={2}
            placeholder="Explique o que está errado e por quê..."
            value={correction}
            onChange={(e) => setCorrection(e.target.value)}
            style={{ marginBottom: 6 }}
          />
          <TextField
            className={classes.correctInput}
            variant="outlined"
            fullWidth
            multiline
            rows={2}
            placeholder="Como deveria responder? (opcional — se vazio, não envia)"
            value={correctResponse}
            onChange={(e) => setCorrectResponse(e.target.value)}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <Button
              className={classes.sendBtn}
              onClick={handleCorrect}
              disabled={loading || (!correction.trim() && !correctResponse.trim())}
              size="small"
            >
              Ensinar
            </Button>
            <Button
              size="small"
              onClick={() => setShowCorrect(false)}
              style={{ color: "#94a3b8", textTransform: "none", fontSize: 12 }}
            >
              Voltar
            </Button>
          </div>
        </>
      ) : (
        <div className={classes.toastActions}>
          <Button
            className={classes.approveBtn}
            onClick={handleApprove}
            disabled={loading}
            startIcon={<Check size={14} />}
            size="small"
          >
            Aprovar
          </Button>
          <Button
            className={classes.correctBtn}
            onClick={() => setShowCorrect(true)}
            disabled={loading}
            startIcon={<MessageSquare size={14} />}
            size="small"
          >
            Corrigir
          </Button>
          <Button
            className={classes.rejectBtn}
            onClick={handleReject}
            disabled={loading}
            startIcon={<X size={14} />}
            size="small"
          >
            Descartar
          </Button>
        </div>
      )}
    </div>
  );
};

const AiApprovalToastListener = () => {
  useStyles(); // Injeta @global styles para o toast dark mode
  const socketManager = useContext(SocketContext);
  const { user } = useContext(AuthContext);
  // Fila de memórias pendentes e ID do toast atualmente exibido
  const queueRef = useRef([]);
  const activeToastIdRef = useRef(null);

  const showNextInQueue = useCallback(() => {
    // Se já tem toast ativo ou fila vazia, não faz nada
    if (activeToastIdRef.current || queueRef.current.length === 0) return;

    const memory = queueRef.current.shift();
    const toastId = `ai-approval-${memory.id}`;
    activeToastIdRef.current = toastId;

    const handleDismiss = () => {
      toast.dismiss(toastId);
      activeToastIdRef.current = null;
      // Mostrar próximo da fila após breve delay
      setTimeout(() => showNextInQueue(), 300);
    };

    toast(
      <ApprovalToastContent
        memory={memory}
        onDismiss={handleDismiss}
      />,
      {
        toastId,
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        position: "bottom-right",
        className: "ai-approval-toast",
        bodyClassName: "ai-approval-toast-body",
        onClose: () => {
          activeToastIdRef.current = null;
          setTimeout(() => showNextInQueue(), 300);
        },
      }
    );
  }, []);

  useEffect(() => {
    if (!user?.companyId) return;

    const companyId = user.companyId;
    const socket = socketManager.getSocket(companyId);

    const handleAiMemory = (data) => {
      if (data.action === "create" && data.memory && user.profile === "admin") {
        // Adiciona na fila e tenta exibir
        queueRef.current.push(data.memory);
        showNextInQueue();
      }

      // Se memória foi aprovada/corrigida/deletada por outro meio, limpar
      if ((data.action === "update" || data.action === "delete") && data.memory) {
        const memoryId = data.memory.id || data.memoryId;
        // Remove da fila se ainda não foi exibida
        queueRef.current = queueRef.current.filter(m => m.id !== memoryId);
        // Se é o toast ativo, dismiss
        const activeId = `ai-approval-${memoryId}`;
        if (activeToastIdRef.current === activeId) {
          toast.dismiss(activeId);
          activeToastIdRef.current = null;
          setTimeout(() => showNextInQueue(), 300);
        }
      }
    };

    socket.on(`company-${companyId}-aiAgentMemory`, handleAiMemory);

    return () => {
      socket.off(`company-${companyId}-aiAgentMemory`, handleAiMemory);
    };
  }, [socketManager, user, showNextInQueue]);

  return null;
};

export default AiApprovalToastListener;
